/**
 * Строки кабинета: каталог, хранение выбора языка, дверь к `t()`.
 *
 * ─── Почему `t()` берут отсюда, а не из общего слоя ─────────────────────────
 *
 * Каталог объявляется здесь, при вычислении этого модуля. Экран, который зовёт
 * `t()` из `@shared/src/i18n` напрямую, может выполниться раньше — и получит
 * ключ вместо строки. Порядок модулей ES гарантирует ровно одно: зависимости
 * вычисляются раньше зависящего. Поэтому `t` **переэкспортирован**: `import
 * { t } from '../i18n'` — это одновременно и функция, и обещание, что каталог
 * уже стоит.
 *
 * React здесь нет намеренно. Этот модуль читают и приёмки (`tools/check-*.mjs`
 * ходят по тому же коду, что работает в кабинете), и модули без разметки —
 * `api/problem.ts`, `ui/labels.ts`. Всё, что про React, лежит рядом, в
 * `react.tsx`.
 *
 * ─── Чей выбор языка ────────────────────────────────────────────────────────
 *
 * Язык кабинета — **настройка учётной записи**, а не язык браузера и не путь в
 * адресе. Так его и держит контракт: `Account.locale` уже есть в
 * `openapi.yaml`, приходит внутри сессии и правится через `PATCH /account`.
 * Человек, открывший кабинет с чужого ноутбука, видит свой язык, а не язык
 * этого ноутбука.
 *
 * До сессии выбирать не из чего, и тогда работает копия последнего выбора в
 * `localStorage`. Нужна она ровно двум местам, и оба настоящие: экран входа
 * (там ещё никто не вошёл) и первый кадр после перезагрузки (сессия придёт
 * ответом, но позже). Копия обновляется из сессии, то есть отстаёт максимум на
 * один заход.
 *
 * `localStorage`, а не `sessionStorage`: язык обязан пережить и выход, и
 * закрытую вкладку — иначе вышедший из кабинета получает форму входа на языке,
 * которого не выбирал. Это обратный случай тому, из-за которого `api/auth.ts`
 * запрещает `localStorage` для сессии: там копия, пережившая выход, — дефект,
 * здесь она и есть смысл.
 */

import {
  market,
  parseLocale,
  registerCatalog,
  setLocale,
  type Locale,
} from '@shared/src/i18n';

import { RU } from './catalogs/ru';

/**
 * Каталог кабинета.
 *
 * Русский приезжает обычным `import`: он стоит в бандле и доступен до первой
 * отрисовки — РФ-кабинет от появления i18n не ждёт ничего. Английский и
 * арабский подтягиваются `import()` отдельными кусками сборки и только по
 * выбору языка, поэтому их строк в РФ-сборке нет вовсе.
 */
registerCatalog('app', (want) => {
  if (want === 'ru') return RU;
  if (want === 'en') return import('./catalogs/en').then((module) => module.EN);
  return import('./catalogs/ar').then((module) => module.AR);
});

export {
  DIRECTION,
  LOCALES,
  LOCALE_NAME,
  hasMessage,
  locale,
  market,
  onLocaleChange,
  t,
  tParts,
  type Locale,
} from '@shared/src/i18n';

const STORAGE_KEY = 'seeno.locale';

/** Последний известный выбор. Приватное окно и запрет хранилища — не сбой. */
function remembered(): Locale | null {
  try {
    return parseLocale(localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

function remember(value: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Хранилище закрыто настройками — язык просто не переживёт вкладку.
  }
}

/** С чего кабинет начинает, пока сессии нет. */
export function initialLocale(): Locale {
  return remembered() ?? market().defaultLocale;
}

/**
 * Поставить язык и запомнить выбор. Единственная дверь для переключателя языка
 * и для того, что придёт из сессии.
 */
export async function chooseLocale(next: Locale): Promise<void> {
  remember(next);
  await setLocale(next);
}
